import { useCallback } from "react";

import { Trans } from "@lingui/react/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { PageHeader } from "@/components/layout/page-header";
import {
  NOTIFICATIONS_VALID_TABS,
  NotificationsView,
  type NotificationsTab,
} from "@/components/notifications/notifications-view";

interface NotificationsSearch {
  tab: NotificationsTab;
}

/**
 * Facility workspace notifications. Legacy rendered the shared
 * `NotificationsClient` with `workspaceType="facility"` +
 * `workspaceId={facilityId}` (facility invitations scoped to staff
 * invites for this facility; feed filtered server-side by workspace).
 */
export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/notifications")({
  validateSearch: (search: Record<string, unknown>): NotificationsSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    return {
      tab: NOTIFICATIONS_VALID_TABS.includes(raw as NotificationsTab)
        ? (raw as NotificationsTab)
        : "all",
    };
  },
  component: FacilityNotificationsPage,
});

function FacilityNotificationsPage() {
  const { facilityId } = Route.useParams();
  const { tab: activeTab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const setActiveTab = useCallback(
    (key: NotificationsTab) => void navigate({ to: ".", search: { tab: key } }),
    [navigate],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PageHeader
        title={<Trans>Notifications</Trans>}
        description={<Trans>All your notifications and invitations in one place</Trans>}
      />
      <NotificationsView
        workspaceType="facility"
        workspaceId={facilityId}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <AccountSecurityBanner />
    </div>
  );
}
